import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Paper,
  Avatar,
  TextField
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { deepOrange } from '@mui/material/colors';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';

/* -----------------------------------
   Definimos componentes con "styled"
----------------------------------- */

// Contenedor principal del chat (dentro de DialogContent)
const ChatPaper = styled(Paper)(({ theme }) => ({
  width: '80vw',
  height: '60vh',
  maxWidth: '100%',
  maxHeight: '700px',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  margin: 'auto',
  padding: 0,
  borderRadius: theme.shape.borderRadius
}));

// Contenedor para la lista de mensajes (con scroll vertical, sin scroll horizontal)
const MessagesBody = styled('div')(() => ({
  width: 'calc(100% - 20px)',
  margin: 10,
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden'
}));

// Formulario que contiene el TextField y el botón de envío
const WrapForm = styled('form')(() => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '95%',
  margin: '0 auto 10px auto'
}));

/** TextField con placeholder, más pequeño, sin label flotante */
const WrapTextField = styled(TextField)(() => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    paddingRight: 0, // quita espacio extra
  },
  '& .MuiOutlinedInput-input': {
    padding: '4px 8px',
    fontSize: '1rem',
    height:'30px'
  }
}));

// Botón de envío con fondo transparente, ícono pequeño
const SendButton = styled('button')(({ theme }) => ({
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  marginLeft: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  padding: 2
}));

/* -----------------------------------
   Estilos para los mensajes (burbujas)
----------------------------------- */

// Contenedor de un mensaje alineado a la IZQUIERDA
const MessageRow = styled('div')(() => ({
  display: 'flex',
  marginBottom: '10px'
}));

// Contenedor de un mensaje alineado a la DERECHA
const MessageRowRight = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginBottom: '10px'
}));

// Burbujas azules (para mensajes “externos”/soporte)
const MessageBlue = styled('div')(() => ({
  position: 'relative',
  marginLeft: '5px',
  padding: '10px',
  backgroundColor: '#A8DDFD',
  width: '70%',
  textAlign: 'left',
  border: '1px solid #97C6E3',
  borderRadius: '10px',
  '&:after': {
    content: "''",
    position: 'absolute',
    width: 0,
    height: 0,
    borderTop: '15px solid #A8DDFD',
    borderLeft: '15px solid transparent',
    borderRight: '15px solid transparent',
    top: 0,
    left: '-15px'
  },
  '&:before': {
    content: "''",
    position: 'absolute',
    width: 0,
    height: 0,
    borderTop: '17px solid #97C6E3',
    borderLeft: '16px solid transparent',
    borderRight: '16px solid transparent',
    top: '-1px',
    left: '-17px'
  }
}));

// Burbujas naranjas (para mensajes propios/usuario)
const MessageOrange = styled('div')(() => ({
  position: 'relative',
  marginRight: '20px',
  padding: '10px',
  backgroundColor: '#f8e896',
  width: '50%',
  textAlign: 'left',
  border: '1px solid #dfd087',
  borderRadius: '10px',
  '&:after': {
    content: "''",
    position: 'absolute',
    width: 0,
    height: 0,
    borderTop: '15px solid #f8e896',
    borderLeft: '15px solid transparent',
    borderRight: '15px solid transparent',
    top: 0,
    right: '-15px'
  },
  '&:before': {
    content: "''",
    position: 'absolute',
    width: 0,
    height: 0,
    borderTop: '17px solid #dfd087',
    borderLeft: '16px solid transparent',
    borderRight: '16px solid transparent',
    top: '-1px',
    right: '-17px'
  }
}));

// Texto dentro de las burbujas
const MessageContent = styled('p')(() => ({
  margin: 0,
  padding: 0,
  lineHeight: '1.3',
  marginBottom: '0' // eliminamos espacio extra
}));

// Avatar “naranja”
const OrangeAvatar = styled(Avatar)(() => ({
  color: '#fff',
  backgroundColor: deepOrange[500],
  width: 28,
  height: 28,
  marginRight: 4
}));

// Avatar “vacío”
const AvatarNothing = styled(Avatar)(() => ({
  color: 'transparent',
  backgroundColor: 'transparent',
  width: 28,
  height: 28,
  marginRight: 4
}));

// Nombre/DisplayName
const DisplayName = styled('div')(() => ({
  marginLeft: '10px',
  fontSize: '0.8em',
  color: '#555'
}));

/* -----------------------------------
   Subcomponentes de mensaje
----------------------------------- */
const MessageLeft = ({ message, photoURL, displayName }) => {
  return (
    <MessageRow>
      {photoURL ? (
        <OrangeAvatar alt={displayName} src={photoURL} />
      ) : (
        <AvatarNothing />
      )}

      <div>
        {displayName && <DisplayName>{displayName}</DisplayName>}
        <MessageBlue>
          <MessageContent>{message}</MessageContent>
        </MessageBlue>
      </div>
    </MessageRow>
  );
};

const MessageRight = ({ message }) => {
  return (
    <MessageRowRight>
      <MessageOrange>
        <MessageContent>{message}</MessageContent>
      </MessageOrange>
    </MessageRowRight>
  );
};

/* -----------------------------------
   Componente principal (SoporteChatModal)
----------------------------------- */
export const SoporteChatModal = ({ open, onClose }) => {
  // Estado de mensajes (ejemplo: uno inicial de “soporte”)
  const [messages, setMessages] = useState([
    {
      sender: 'soporte',
      text: 'Hola, ¿en qué te podemos ayudar?',
      displayName: 'Soporte',
      photoURL: '' // O una URL si quieres un avatar
    }
  ]);

  // Estado para el nuevo texto
  const [newMessage, setNewMessage] = useState('');

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;

    // Agregamos mensaje del "usuario"
    setMessages((prev) => [
      ...prev,
      {
        sender: 'usuario',
        text: newMessage,
        displayName: '',
        photoURL: ''
      }
    ]);
    setNewMessage('');
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {/* Título con botón de cierre en la esquina superior derecha */}
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        Chat de Soporte
        <IconButton
          onClick={onClose}
          sx={{ marginLeft: 'auto' }}
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ padding: 0 }}>
        <ChatPaper elevation={2}>
          <MessagesBody>
            {messages.map((msg, idx) =>
              msg.sender === 'soporte' ? (
                <MessageLeft
                  key={idx}
                  message={msg.text}
                  photoURL={msg.photoURL}
                  displayName={msg.displayName}
                />
              ) : (
                <MessageRight key={idx} message={msg.text} />
              )
            )}
          </MessagesBody>

          <WrapForm
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
          >
            <WrapTextField
              placeholder="Escribe tu mensaje"
              variant="outlined"
              size="small"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
            />
            <SendButton onClick={handleSendMessage}>
              <SendIcon fontSize="small" sx={{ color: 'blue' }} />
            </SendButton>
          </WrapForm>
        </ChatPaper>
      </DialogContent>
    </Dialog>
  );
};
